import React from "react";
import { Tooltip } from "@mui/material";
import { attentionColors } from "../themes/mobiliTheme";

export interface GapDaysProps {
  /** Days between the affected appointment and its neighbour; null = unknown. */
  gapInDays?: number | null;
  /**
   * Whether the affected appointment was the first / last of its prescription
   * (`firstLastCancelled` on the worklist rows). Both can be true.
   */
  firstMatchesCancelled?: boolean;
  lastMatchesCancelled?: boolean;
}

/**
 * Severity colour for a gap of `weeks` weeks — the longer a patient has been
 * waiting, the hotter the colour. Shared by the dashboard worklists; the same
 * thresholds previously lived (twice) inside the two widgets.
 */
export const gapSeverityColor = (weeks: number): string => {
  if (weeks <= 1) return attentionColors.customInfoPrimary;
  if (weeks <= 2) return attentionColors.lowPriority;
  if (weeks <= 3) return attentionColors.highPriority;
  return attentionColors.immediateAttention;
};

/**
 * The "Lücke" cell of the dashboard worklists: days waited, as a number next
 * to a severity dot.
 *
 * Replaces a colour-filled 30px Avatar with two MUI Badges absolutely
 * positioned half outside it. The three facts survive, none as boxes
 * (design-system rule 4 — colour on a dot, not a fill):
 *
 * - **severity** → the dot, same thresholds as before;
 * - **days** → plain tabular-nums text;
 * - **first/last of the Rx affected** → inline E/L markers with tooltips,
 *   instead of floating badges that overlapped neighbouring cells.
 */
export const GapDays: React.FC<GapDaysProps> = ({
  gapInDays,
  firstMatchesCancelled,
  lastMatchesCancelled,
}) => {
  const hasGap = gapInDays !== null && gapInDays !== undefined;

  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <Tooltip title={hasGap ? `Lücke von ${gapInDays} Tagen` : "Lücke unbekannt"}>
        <span className="inline-flex items-center gap-1 text-dense font-semibold tabular-nums text-ink-text">
          <span
            aria-hidden
            className="h-2 w-2 shrink-0 rounded-pill"
            style={{
              backgroundColor: hasGap ? gapSeverityColor(gapInDays! / 7) : "transparent",
              border: hasGap ? "none" : "1px solid rgb(var(--ink) / 0.25)",
            }}
          />
          {hasGap ? gapInDays : "–"}
        </span>
      </Tooltip>
      {firstMatchesCancelled && (
        <Tooltip title="Erster Termin des Rezepts betroffen">
          <span className="text-micro font-bold text-ink-muted">E</span>
        </Tooltip>
      )}
      {lastMatchesCancelled && (
        <Tooltip title="Letzter Termin des Rezepts betroffen">
          <span className="text-micro font-bold text-ink-muted">L</span>
        </Tooltip>
      )}
    </span>
  );
};

export default GapDays;
